import { RefObject, useCallback, useLayoutEffect, useState } from 'react';

type UseAvailableHeightOptions = {
  /** Ondergrens, zodat een element nooit volledig wegvalt. */
  minHeight?: number;
  /** Extra elementen die worden geobserveerd, bijvoorbeeld een sectie erboven die in- en uitklapt. */
  observedRefs?: RefObject<HTMLElement | null>[];
  /** Waarden die een herberekening afdwingen, bijvoorbeeld open/dicht-state. */
  deps?: unknown[];
};

/**
 * Berekent hoeveel ruimte er binnen `containerRef` overblijft onder de bovenkant van `elementRef`.
 *
 * Bedoeld om te gebruiken als `max-height` en niet als `height`: het element blijft zo hoog als zijn inhoud, maar
 * nooit hoger dan de beschikbare ruimte. Een harde `max-height` is daarnaast betrouwbaarder dan flex-fill wanneer een
 * bovenliggend component de hoogte meet of animeert (zoals een Radix Collapsible), omdat de gemeten hoogte dan gelijk
 * is aan wat er daadwerkelijk getoond wordt.
 *
 * Herberekent via een ResizeObserver op de container en de meegegeven `observedRefs`.
 *
 * ```ts
 * const maxHeight = useAvailableHeight(wrapperRef, listRef, { observedRefs: [headerRef], deps: [isOpen] });
 * <div ref={listRef} style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined} />
 * ```
 */
const useAvailableHeight = (
  containerRef: RefObject<HTMLElement | null>,
  elementRef: RefObject<HTMLElement | null>,
  { minHeight = 0, observedRefs = [], deps = [] }: UseAvailableHeightOptions = {},
) => {
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const element = elementRef.current;
    if (!container || !element) {
      setAvailableHeight(null);
      return;
    }
    const offsetTop = element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    setAvailableHeight(Math.max(container.clientHeight - offsetTop, minHeight));
  }, [containerRef, elementRef, minHeight]);

  useLayoutEffect(() => {
    measure();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    [containerRef, ...observedRefs].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, [measure, ...deps]);

  return availableHeight;
};

export default useAvailableHeight;
